import type {
  GetApiPayloadByUrl,
  GetApiResponseByUrl,
} from '@crossbeam/openapi';

import {
  getOperatorLabel,
  getOperatorLabelAnyType,
} from '@/constants/filter-operators';
import type { useTeamStore } from '@/stores/TeamStore';
import type {
  FilterFieldOption,
  FilterRowState,
  ReportFilterOperatorTypes,
} from '@/types/filters';
import { ALL_SEGMENTS, SEGMENTS_TO_LABEL_MAP } from '@/types/populations';

export type Agent = GetApiResponseByUrl<'/v0.2/agents'>['data'][number];

type CreatedByUser = ReturnType<ReturnType<typeof useTeamStore>['getUserById']>;
export type EnrichedAgent = Agent & { createdByUser: CreatedByUser };

type AgentNotificationChannel = Agent['notification_channels'][number];

export function getAgentNotificationChannels(agent: {
  notification_channel?: AgentNotificationChannel | null;
  notification_channels?: AgentNotificationChannel[];
}): AgentNotificationChannel[] {
  if (agent.notification_channel) return [agent.notification_channel];
  return agent.notification_channels ?? [];
}

export type CreateAgentPayload = GetApiPayloadByUrl<'/v0.2/agents', 'post'>;

export type UpdateAgentPayload = GetApiPayloadByUrl<
  '/v0.2/agents/{id}',
  'patch'
>;

export type TestAgentPayload = GetApiPayloadByUrl<'/v0.2/agents-test', 'post'>;

export type AgentActionType =
  | 'slack_channel'
  | 'slack_channel_partner'
  | 'slack_dm';

export type SlackUser =
  GetApiResponseByUrl<'/v0.1/slack-app/users'>['users'][number];

export type AgentCondition = Agent['conditions'][number];

export const UI_TO_API_OP: Partial<
  Record<ReportFilterOperatorTypes, AgentCondition['op']>
> = {
  in: '=',
  'not in': '!=',
};

export const API_TO_UI_OP: Partial<
  Record<AgentCondition['op'], ReportFilterOperatorTypes>
> = {
  '=': 'in',
  '!=': 'not in',
};

export const PARTNER_POPULATION_SEGMENTS_FIELD: FilterFieldOption<string> = {
  label: 'Partner population',
  value: 'partner_population_segments',
  dataType: 'string',
  initialOptionsExist: true,
  preserveOptionOrder: true,
  options: ALL_SEGMENTS.map((segment) => ({
    label: SEGMENTS_TO_LABEL_MAP[segment],
    value: segment,
  })),
  allowedOperators: ['in'],
};

export const PARTNER_SCORE_LEVEL_FIELD: FilterFieldOption<string> = {
  label: 'Partner score level',
  value: 'partner_score_bucket',
  dataType: 'string',
  initialOptionsExist: true,
  preserveOptionOrder: true,
  options: [
    { label: 'High', value: 'high' },
    { label: 'Medium', value: 'medium' },
    { label: 'Low', value: 'low' },
    { label: 'None', value: 'none' },
    { label: 'Unknown', value: 'unknown' },
  ],
  allowedOperators: ['in', 'not in'],
};

export type AgentFormState = Pick<
  CreateAgentPayload,
  'name' | 'message_template'
> & {
  trigger_type?: TriggerType;
  notification_channel_type?: AgentActionType;
  notification_channels: NonNullable<
    CreateAgentPayload['notification_channels']
  >;
  conditions: ConditionRow[];
};

export const EMPTY_AGENT_FORM_STATE: AgentFormState = {
  name: '',
  trigger_type: undefined,
  notification_channel_type: undefined,
  notification_channels: [],
  message_template: '',
  conditions: [],
};

export type AgentRegistryResponse =
  GetApiResponseByUrl<'/v0.2/agents-registry'>;

export type AgentRegistryTrigger =
  AgentRegistryResponse['data']['triggers'][number];

export type AgentTriggerFilter = AgentRegistryTrigger['filters'][number];

export type TriggerType = AgentRegistryTrigger['trigger_type'];

export const TRIGGER_OPTIONS = [
  { value: 'new_mutual_customer', label: 'New mutual customer' },
  { value: 'new_shared_account', label: 'New mutual account' },
  {
    value: 'partner_opened_opp',
    label: 'Partner opened a deal on a mutual account',
  },
  {
    value: 'partner_won_competing_deal',
    label: 'Partner closes a deal won on an account you have as an active opp',
  },
  {
    value: 'partner_won_shared_deal',
    label: 'Partner won a deal on a mutual account',
  },
] as const satisfies readonly {
  value: TriggerType;
  label: string;
  isHidden?: boolean;
}[];

export const TRIGGER_TYPE_LABELS = Object.fromEntries(
  TRIGGER_OPTIONS.map((item) => [item.value, item.label]),
) as Partial<Record<TriggerType, string>>;

export function triggerLabel(triggerType: TriggerType): string {
  return (
    TRIGGER_TYPE_LABELS[triggerType] ??
    triggerType.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
  );
}

export const ACTION_OPTIONS = [
  { value: 'slack_channel', label: 'Send Slack message to channel' },
  {
    value: 'slack_channel_partner',
    label: 'Send Slack message to partner (Slack Connect)',
  },
  { value: 'slack_dm', label: 'Send direct message' },
] as const satisfies readonly {
  value: AgentActionType;
  label: string;
}[];

export type TemplatePrefill = {
  trigger_type: TriggerType;
  name?: string;
};

export const TEMPLATE_PREFILLS: Record<string, TemplatePrefill> = {
  'We share a new customer': {
    trigger_type: 'new_mutual_customer',
    name: 'We share a new customer',
  },
};

export type CellAction = 'edit' | 'delete';

export type AgentsTableContext = {
  actionTaken: (action: CellAction, ctx: string) => void;
};

export type ConditionRow = {
  id: string;
  state: FilterRowState<string, unknown>;
};

export function resolveConditionFieldOptions(
  triggerType: TriggerType | undefined,
  conditionFields: Iterable<string | undefined>,
  fieldOptionsByTriggerType: Partial<
    Record<TriggerType, FilterFieldOption<string>[]>
  >,
  fallbackFieldOptionByField: Map<string, FilterFieldOption<string>>,
): FilterFieldOption<string>[] {
  if (!triggerType) return [];
  const registryOptions = fieldOptionsByTriggerType[triggerType] ?? [];
  if (registryOptions.length) return registryOptions;

  const seen = new Set<string>();
  const fallbacks: FilterFieldOption<string>[] = [];
  for (const field of conditionFields) {
    if (!field || seen.has(field)) continue;
    const option = fallbackFieldOptionByField.get(field);
    if (option) {
      seen.add(field);
      fallbacks.push(option);
    }
  }
  return fallbacks;
}

export function formatCondition(
  condition: AgentCondition,
  fieldOptions: FilterFieldOption<string>[],
): string {
  const fieldOption = fieldOptions.find((o) => o.value === condition.field);
  const fieldLabel = fieldOption?.label ?? condition.field;
  const uiOp = API_TO_UI_OP[condition.op] ?? condition.op;
  const opLabel = fieldOption?.dataType
    ? getOperatorLabel(fieldOption.dataType, uiOp)
    : getOperatorLabelAnyType(uiOp);
  const rawValues = Array.isArray(condition.value)
    ? condition.value
    : condition.value != null
      ? [condition.value]
      : [];
  const valueLabels = rawValues
    .map((v) => {
      const opt = fieldOption?.options?.find(
        (o) => String(o.value) === String(v),
      );
      return opt?.label ?? String(v);
    })
    .join(', ');
  return `${fieldLabel} ${opLabel} ${valueLabels}`.trimEnd();
}
